import { useChartSeries } from '@/lib/theme/useThemeColors';

interface ColorSwatchPickerProps {
  /** Current color as a hex string (legacy values may be uppercase or off-palette). */
  value: string;
  /** Fires with a #rrggbb hex — preset pick or native-picker input. */
  onChange: (hex: string) => void;
}

// input[type=color] rejects anything but a strict #rrggbb value; guard against
// malformed legacy data so the browser doesn't warn and coerce to black silently.
function toInputHex(value: string): string {
  return /^#[0-9a-f]{6}$/i.test(value) ? value : '#000000';
}

/**
 * Color choices for a custom category: the 10 chart-series hues (theme tokens
 * resolved to hex at pick time) plus a native color picker for anything else.
 * Values are always stored as the resolved #rrggbb string, so preset and
 * custom picks stay consistent.
 */
export function ColorSwatchPicker({ value, onChange }: ColorSwatchPickerProps) {
  const presets = useChartSeries();
  const normalized = value.toLowerCase();
  const isCustom = !presets.some((p) => p.toLowerCase() === normalized);

  return (
    // p-0.5: the selected swatch scales to 110% and would otherwise poke past
    // the sheet's scroll container, whose left overflow is clipped (not
    // scrollable) — the first bubble showed up with a flat-cut edge.
    <div className="flex flex-wrap gap-2 p-0.5">
      {presets.map((c) => (
        <button
          key={c}
          type="button"
          aria-label={`Cor ${c}`}
          aria-pressed={c.toLowerCase() === normalized}
          onClick={() => onChange(c)}
          className={`size-7 rounded-full border-2 transition-transform ${
            c.toLowerCase() === normalized ? 'scale-110 border-text-primary' : 'border-transparent'
          }`}
          style={{ backgroundColor: c }}
        />
      ))}
      <label
        className={`relative size-7 cursor-pointer rounded-full border-2 transition-transform has-focus-visible:ring-2 has-focus-visible:ring-ring has-focus-visible:ring-offset-2 ${
          isCustom ? 'scale-110 border-text-primary' : 'border-transparent'
        }`}
        style={
          isCustom
            ? { backgroundColor: value }
            : { background: `conic-gradient(${[...presets, presets[0]].join(', ')})` }
        }
      >
        {/* opacity-0 overlay instead of hiding: iOS Safari won't open the native
            picker for a display:none/1px input, and the overlay keeps focus and
            keyboard activation on a real input box. */}
        <input
          type="color"
          value={toInputHex(value)}
          onChange={(e) => onChange(e.target.value)}
          aria-label="Cor personalizada"
          className="absolute inset-0 size-full cursor-pointer opacity-0"
        />
      </label>
    </div>
  );
}
